import React, { useMemo, useState } from 'react';
import Icon from '../components/Icon';

type MaintStatus = 'Pending' | 'In Progress' | 'Completed';
type Urgency = 'Low' | 'Medium' | 'High';

interface MaintRequest {
  id: number;
  title: string;
  category: string;
  property: string;
  tenant: string;
  phone: string;
  email: string;
  status: MaintStatus;
  urgency: Urgency;
  date: string;
  time: string;
  desc: string;
  photos: number;
  adminNote: string;
}

const REQUESTS: MaintRequest[] = [
  { id: 1, title: 'Leaking kitchen faucet',     category: 'Plumbing',   property: 'Modern 2BR Apartment',     tenant: 'Jason Mitchell',     phone: '(313) 492-4821', email: 'jason.mitchell@gmail.com', status: 'In Progress', urgency: 'Medium', date: 'May 8, 2026', time: '10:15 AM', desc: 'Persistent drip from the kitchen faucet, started 3 days ago. Getting worse.',                                       photos: 1, adminNote: 'Assigned plumber: Mike R. — ETA Friday PM.' },
  { id: 2, title: 'Broken bathroom exhaust fan', category: 'Electrical', property: 'Modern 2BR Apartment',    tenant: 'Current Tenant',     phone: '(313) 492-4821', email: 'tenant@email.com',         status: 'Pending',     urgency: 'Low',    date: 'May 7, 2026', time: '3:42 PM',  desc: "Exhaust fan in main bathroom makes grinding noise and doesn't spin. Moisture building up.",                       photos: 2, adminNote: 'Awaiting parts — will schedule once available.' },
  { id: 3, title: 'HVAC not cooling',            category: 'HVAC',       property: 'Cozy 1BR Near Campus',    tenant: 'Laura Perez',        phone: '(248) 443-5534', email: 'laura.perez@hotmail.com',  status: 'Pending',     urgency: 'High',   date: 'May 6, 2026', time: '8:30 AM',  desc: 'AC blowing warm air only. Thermostat set to 68 but reads 79. Filter was replaced last month.',                    photos: 0, adminNote: 'High priority — summer heat. Scheduled tech visit Mon 5/12.' },
  { id: 4, title: 'Move-out deep cleaning',      category: 'Cleaning',   property: 'Spacious Family Home',    tenant: 'N/A — Vacancy prep', phone: '',               email: '',                         status: 'Completed',   urgency: 'Medium', date: 'May 2, 2026', time: '9:00 AM',  desc: 'Full move-out cleaning requested before next tenant. Kitchen, bathrooms, carpets, windows.',                       photos: 3, adminNote: 'Completed by CleanPro team on 5/4. Photos uploaded.' },
  { id: 5, title: 'Exterior paint touch-up',     category: 'Renovation', property: 'Renovated Brick Colonial', tenant: 'N/A — Owner request', phone: '',              email: '',                         status: 'Completed',   urgency: 'Low',    date: 'Apr 28, 2026', time: '11:00 AM', desc: 'Front porch trim and window frames need repainting — chipping from winter.',                                       photos: 2, adminNote: 'Done 5/1. Color matched: Benjamin Moore HC-172.' },
];

type Filter = 'All' | MaintStatus;
const FILTERS: Filter[] = ['All', 'Pending', 'In Progress', 'Completed'];

const statusStyle = (s: MaintStatus): React.CSSProperties => {
  if (s === 'Pending')     return { background: 'rgba(217,119,6,.10)',  color: 'var(--yellow)' };
  if (s === 'In Progress') return { background: 'rgba(37,99,235,.10)',  color: 'var(--blue)' };
  return                          { background: 'rgba(5,150,105,.10)',  color: 'var(--green)' };
};

const urgencyStyle = (u: Urgency): React.CSSProperties => {
  if (u === 'High')   return { background: '#FEE2E2', color: '#DC2626' };
  if (u === 'Medium') return { background: '#FEF3C7', color: '#D97706' };
  return                     { background: '#DBEAFE', color: '#2563EB' };
};

const AgentMaintenance: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('All');
  const [selected, setSelected] = useState<number | null>(REQUESTS[0].id);

  const filtered = useMemo<MaintRequest[]>(() => {
    if (filter === 'All') return REQUESTS;
    return REQUESTS.filter((r) => r.status === filter);
  }, [filter]);

  const current = REQUESTS.find((r) => r.id === selected) || null;

  const pendingCount = REQUESTS.filter((r) => r.status === 'Pending').length;
  const inProgressCount = REQUESTS.filter((r) => r.status === 'In Progress').length;

  return (
    <div>
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6 md:[grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        {[
          { icon: 'wrench',         label: 'Total Requests', value: String(REQUESTS.length), bg: 'var(--brand-bg)',         color: 'var(--brand)' },
          { icon: 'bell',           label: 'Pending',        value: String(pendingCount),    bg: 'rgba(217,119,6,.10)',     color: 'var(--yellow)' },
          { icon: 'package',        label: 'In Progress',    value: String(inProgressCount), bg: 'rgba(37,99,235,.10)',     color: 'var(--blue)' },
          { icon: 'check-circle',   label: 'Completed',      value: String(REQUESTS.filter((r) => r.status === 'Completed').length), bg: 'rgba(5,150,105,.10)', color: 'var(--green)' },
        ].map((s) => (
          <div key={s.label} className="card min-w-0 p-3.5 sm:p-[18px]" style={{ borderRadius: 16 }}>
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div
                className="rounded-xl flex items-center justify-center flex-shrink-0 w-9 h-9 sm:w-[42px] sm:h-[42px]"
                style={{ background: s.bg }}
              >
                <Icon name={s.icon} size={20} color={s.color} />
              </div>
              <div className="min-w-0">
                <div
                  className="font-extrabold text-ink leading-tight"
                  style={{ fontSize: 22, letterSpacing: '-0.4px' }}
                >
                  {s.value}
                </div>
                <div className="text-g500" style={{ fontSize: 12.5 }}>
                  {s.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex gap-1.5 mb-4 sm:mb-5 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible">
        {FILTERS.map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              className="flex-shrink-0 whitespace-nowrap"
              onClick={() => setFilter(f)}
              style={{
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                border: '1px solid ' + (active ? 'var(--brand)' : 'var(--g200)'),
                background: active ? 'var(--brand-light)' : '#fff',
                color: active ? 'var(--brand)' : 'var(--g600)',
                cursor: 'pointer',
              }}
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* Two-column */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-4 sm:gap-5">
        {/* List */}
        <div className="card min-w-0" style={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}>
          {filtered.map((r) => {
            const active = selected === r.id;
            return (
              <div
                key={r.id}
                onClick={() => setSelected(r.id)}
                className="cursor-pointer px-4 py-3.5 sm:px-[18px]"
                style={{
                  borderBottom: '1px solid var(--g100)',
                  background: active ? 'var(--brand-light)' : 'transparent',
                  borderLeft:
                    '3px solid ' + (active ? 'var(--brand)' : 'transparent'),
                }}
              >
                <div className="flex justify-between items-start gap-2 mb-1">
                  <div
                    className="font-semibold text-ink"
                    style={{ fontSize: 14 }}
                  >
                    {r.title}
                  </div>
                  <span
                    className="font-semibold whitespace-nowrap"
                    style={{
                      fontSize: 10.5,
                      padding: '2px 8px',
                      borderRadius: 100,
                      ...statusStyle(r.status),
                    }}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="text-g500 mb-2" style={{ fontSize: 12 }}>
                  {r.property}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    style={{
                      fontSize: 10.5,
                      padding: '2px 8px',
                      borderRadius: 100,
                      background: 'var(--g100)',
                      color: 'var(--g600)',
                      fontWeight: 600,
                    }}
                  >
                    {r.category}
                  </span>
                  <span
                    className="font-semibold"
                    style={{
                      fontSize: 10.5,
                      padding: '2px 8px',
                      borderRadius: 100,
                      ...urgencyStyle(r.urgency),
                    }}
                  >
                    {r.urgency}
                  </span>
                  <span className="text-g400 ml-auto" style={{ fontSize: 11.5 }}>
                    {r.date}
                  </span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-g500" style={{ fontSize: 13 }}>
              No requests
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="card min-w-0" style={{ padding: 0, borderRadius: 16 }}>
          {!current ? (
            <div className="flex flex-col items-center justify-center text-center" style={{ padding: '80px 32px' }}>
              <div
                className="rounded-[20px] flex items-center justify-center mb-4"
                style={{ width: 72, height: 72, background: 'var(--g100)' }}
              >
                <Icon name="wrench" size={32} color="var(--g400)" />
              </div>
              <h3 className="font-bold text-ink mb-1" style={{ fontSize: 17 }}>
                Select a request
              </h3>
            </div>
          ) : (
            <>
              <div
                className="flex items-start justify-between gap-3 px-4 py-4 sm:px-6 sm:py-5"
                style={{ borderBottom: '1px solid var(--g100)' }}
              >
                <div className="min-w-0">
                  <h2
                    className="font-extrabold text-ink mb-1"
                    style={{ fontSize: 19, letterSpacing: '-0.3px' }}
                  >
                    {current.title}
                  </h2>
                  <div className="flex gap-2 items-center flex-wrap">
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 9px',
                        borderRadius: 100,
                        background: 'var(--g100)',
                        color: 'var(--g600)',
                        fontWeight: 600,
                      }}
                    >
                      {current.category}
                    </span>
                    <span
                      className="font-semibold"
                      style={{
                        fontSize: 11,
                        padding: '2px 9px',
                        borderRadius: 100,
                        ...urgencyStyle(current.urgency),
                      }}
                    >
                      {current.urgency} urgency
                    </span>
                  </div>
                </div>
                <span
                  className="font-semibold whitespace-nowrap"
                  style={{
                    fontSize: 11.5,
                    padding: '4px 12px',
                    borderRadius: 100,
                    ...statusStyle(current.status),
                  }}
                >
                  {current.status}
                </span>
              </div>

              <div className="px-4 py-4 sm:px-6 sm:py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <div>
                    <div className="font-semibold text-g400 uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.6px' }}>
                      Property
                    </div>
                    <div className="text-ink" style={{ fontSize: 13.5 }}>
                      {current.property}
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-g400 uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.6px' }}>
                      Tenant
                    </div>
                    <div className="text-ink" style={{ fontSize: 13.5 }}>
                      {current.tenant}
                    </div>
                  </div>
                  {current.phone && (
                    <div>
                      <div className="font-semibold text-g400 uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.6px' }}>
                        Phone
                      </div>
                      <a
                        href={'tel:' + current.phone}
                        className="hover:underline"
                        style={{ fontSize: 13.5, color: 'var(--brand)' }}
                      >
                        {current.phone}
                      </a>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-g400 uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.6px' }}>
                      Submitted
                    </div>
                    <div className="text-ink" style={{ fontSize: 13.5 }}>
                      {current.date} · {current.time}
                    </div>
                  </div>
                </div>

                <div className="mb-5">
                  <div className="font-semibold text-g400 uppercase mb-2" style={{ fontSize: 11, letterSpacing: '0.6px' }}>
                    Description
                  </div>
                  <div
                    className="leading-relaxed text-g700"
                    style={{
                      fontSize: 13.5,
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: 'var(--g50)',
                      border: '1px solid var(--g100)',
                    }}
                  >
                    {current.desc}
                  </div>
                  {current.photos > 0 && (
                    <div className="text-g400 mt-2" style={{ fontSize: 12 }}>
                      <Icon name="package" size={12} color="var(--g400)" /> {current.photos} photo
                      {current.photos > 1 ? 's' : ''} attached
                    </div>
                  )}
                </div>

                {current.adminNote && (
                  <div
                    style={{
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: 'var(--brand-light)',
                      border: '1px solid rgba(225,1,116,.12)',
                    }}
                  >
                    <div
                      className="font-semibold uppercase mb-1"
                      style={{ fontSize: 11, letterSpacing: '0.6px', color: 'var(--brand)' }}
                    >
                      Admin note
                    </div>
                    <div className="text-g700" style={{ fontSize: 13.5 }}>
                      {current.adminNote}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className="mt-5 px-4 py-3 flex items-start gap-2"
        style={{
          borderRadius: 12,
          background: 'var(--blue-bg)',
          border: '1px solid rgba(37,99,235,.12)',
          color: 'var(--blue)',
          fontSize: 13,
        }}
      >
        <Icon name="bell" size={16} color="var(--blue)" />
        <div>
          <strong>Demo data:</strong> These maintenance requests are samples
          until backend endpoints are wired up.
        </div>
      </div>
    </div>
  );
};

export default AgentMaintenance;
